import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Paintbrush, ShieldCheck, Clock, Leaf, ArrowRight, Star, Quote, CheckCircle, ZoomIn, ChevronLeft, ChevronRight, Phone, Send, AlertCircle, Mail, MapPin } from 'lucide-react'
import ScrollReveal from '../components/ScrollReveal'
import BeforeAfterSlider from '../components/BeforeAfterSlider'

export default function Home() {
  const [services, setServices] = useState([])
  const [galleryItems, setGalleryItems] = useState([])
  const [testimonials, setTestimonials] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAllServices, setShowAllServices] = useState(false)
  const [activeTestimonial, setActiveTestimonial] = useState(0)

  // Hero Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Indendørs Maling',
    message: '',
    website_url: '' // Honeypot
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' or 'error'
  const [submitMessage, setSubmitMessage] = useState('')

  // Bottom Form State
  const [bottomFormData, setBottomFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Indendørs Maling',
    message: '',
    website_url: '' // Honeypot
  })
  const [bottomSubmitting, setBottomSubmitting] = useState(false)
  const [bottomSubmitStatus, setBottomSubmitStatus] = useState(null) // 'success' or 'error'
  const [bottomSubmitMessage, setBottomSubmitMessage] = useState('')

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleBottomInputChange = (e) => {
    const { name, value } = e.target
    setBottomFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitStatus(null)
    setSubmitMessage('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const result = await response.json()

      if (response.ok && result.status === 'ok') {
        setSubmitStatus('success')
        setSubmitMessage('Mange tak! Vi har modtaget din forespørgsel og vender tilbage med et uforpligtende tilbud inden for 24 timer.')
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: 'Indendørs Maling',
          message: '',
          website_url: ''
        })
      } else {
        setSubmitStatus('error')
        setSubmitMessage(result.message || 'Der opstod en fejl under indsendelsen. Prøv venligst igen eller ring til os.')
      }
    } catch (error) {
      console.error('Hero form submission error:', error)
      setSubmitStatus('error')
      setSubmitMessage('Der opstod en netværksfejl. Prøv venligst igen.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleBottomFormSubmit = async (e) => {
    e.preventDefault()
    setBottomSubmitting(true)
    setBottomSubmitStatus(null)
    setBottomSubmitMessage('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(bottomFormData)
      })

      const result = await response.json()

      if (response.ok && result.status === 'ok') {
        setBottomSubmitStatus('success')
        setBottomSubmitMessage('Mange tak! Vi har modtaget din forespørgsel og vender tilbage med et uforpligtende tilbud inden for 24 timer.')
        setBottomFormData({
          name: '',
          email: '',
          phone: '',
          subject: 'Indendørs Maling',
          message: '',
          website_url: ''
        })
      } else {
        setBottomSubmitStatus('error')
        setBottomSubmitMessage(result.message || 'Der opstod en fejl under indsendelsen. Prøv venligst igen eller ring til os.')
      }
    } catch (error) {
      console.error('Bottom form submission error:', error)
      setBottomSubmitStatus('error')
      setBottomSubmitMessage('Der opstod en netværksfejl. Prøv venligst igen.')
    } finally {
      setBottomSubmitting(false)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, galleryRes, testimonialsRes] = await Promise.all([
          fetch('https://api.dev.auxo.dk/public/content/services'),
          fetch('https://api.dev.auxo.dk/public/content/gallery_items'),
          fetch('https://api.dev.auxo.dk/public/content/testimonials')
        ])

        if (servicesRes.ok) {
          const data = await servicesRes.json()
          setServices(data.slice(0, 4))
        }
        if (galleryRes.ok) {
          const data = await galleryRes.json()
          setGalleryItems(data.slice(0, 3))
        }
        if (testimonialsRes.ok) {
          const data = await testimonialsRes.json()
          setTestimonials(data)
        }
      } catch (error) {
        console.error('Fejl ved hentning af CMS data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const usps = [
    {
      icon: <i className="fa-duotone fa-solid fa-paint-roller text-3xl text-sky-500" />,
      title: 'Højeste Faglighed',
      description: 'Vores uddannede malere har mange års erfaring og kender de helt rigtige teknikker og materialer til din opgave.'
    },
    {
      icon: <i className="fa-duotone fa-solid fa-broom text-3xl text-sky-500" />,
      title: 'Rent & Pænt Arbejde',
      description: 'Vi sørger for grundig afdækning og rydder altid pænt op efter os hver dag, så dit hjem forbliver rent og rart.'
    },
    {
      icon: <i className="fa-duotone fa-solid fa-shield-check text-3xl text-sky-500" />,
      title: 'Kvalitetsgaranti',
      description: 'Vi sikrer dig altid et perfekt malerarbejde og forlader aldrig opgaven, før du er fuldstændig tilfreds med resultatet.'
    },
    {
      icon: <i className="fa-duotone fa-solid fa-leaf text-3xl text-sky-500" />,
      title: 'Miljø & Indeklima',
      description: 'Vi bruger svanemærkede og lugtsvage kvalitetsprodukter, som skåner miljøet og sikrer et sundt indeklima i dit hjem.'
    }
  ]

  // Fallback data if CMS is empty or loading
  const fallbackServices = [
    {
      id: '1',
      title: { da: 'Indendørs Maling' },
      description: { da: 'Professionel maling af vægge, lofter, døre og paneler med fokus på detaljen.' },
      icon: 'Paintbrush',
      image: ['https://assets.auxodev.dk/assets/8102f19c-aab0-44cc-aa15-a120b6503274/548826e2-2df9-4b98-b3e3-fdc54ae27250.jpg']
    },
    {
      id: '2',
      title: { da: 'Udendørs Maling' },
      description: { da: 'Vejrbestandig beskyttelse og forskønnelse af facader, træværk og vinduer.' },
      icon: 'Home',
      image: ['https://assets.auxodev.dk/assets/8102f19c-aab0-44cc-aa15-a120b6503274/231001d8-c66e-4260-89d9-639a8d0e2454.jpg']
    },
    {
      id: '3',
      title: { da: 'Tapetsering & Filt' },
      description: { da: 'Opsætning af filt, væv og tapet med millimeterpræcision for glatte vægge.' },
      icon: 'Sparkles',
      image: ['https://assets.auxodev.dk/assets/8102f19c-aab0-44cc-aa15-a120b6503274/ad8e9954-2922-48c4-bfdb-871cbf625d14.jpg']
    }
  ]

  const displayServices = services.length > 0 ? services : fallbackServices
  const visibleServices = showAllServices ? displayServices : displayServices.slice(0, 2)
  const displayGallery = galleryItems.length > 0 ? galleryItems : []
  const displayTestimonials = testimonials.length > 0 ? testimonials : []

  const nextTestimonial = () => {
    if (displayTestimonials.length === 0) return
    setActiveTestimonial((prev) => (prev + 1) % displayTestimonials.length)
  }

  const prevTestimonial = () => {
    if (displayTestimonials.length === 0) return
    setActiveTestimonial((prev) => (prev - 1 + displayTestimonials.length) % displayTestimonials.length)
  }

  // Get the 3 testimonials to display based on the active index
  const getVisibleTestimonials = () => {
    if (displayTestimonials.length === 0) return []
    const items = []
    const count = Math.min(3, displayTestimonials.length)
    for (let i = 0; i < count; i++) {
      const index = (activeTestimonial + i) % displayTestimonials.length
      items.push({ ...displayTestimonials[index], displayKey: `${displayTestimonials[index].id}-${i}` })
    }
    return items
  }

  const visibleTestimonials = getVisibleTestimonials()

  useEffect(() => {
    if (displayTestimonials.length <= 1) return
    const interval = setInterval(() => {
      nextTestimonial()
    }, 6000)
    return () => clearInterval(interval)
  }, [displayTestimonials.length])

  return (
    <div className="bg-slate-50">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-slate-950 py-16 lg:py-24">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/109aefc0-8d55-4828-91bb-df83d88851f4.jpg"
            alt="Professionel maler i gang med malerarbejde"
            className="h-full w-full object-cover object-center"
          />
          {/* Dark Overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/75 via-slate-950/55 to-slate-950/65 backdrop-blur-[1px]" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Text Content */}
            <div className="lg:col-span-7 text-center lg:text-left space-y-6">
              <h1 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                Professionelt malerarbejde med garanti
              </h1>
              <p className="text-base sm:text-lg text-slate-200 leading-relaxed max-w-2xl mx-auto lg:mx-0" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                Hos Probyg Maler leverer vi kompromisløs kvalitet til både private og erhverv. Uanset om det gælder en enkelt stue, en hel facade eller et større erhvervsbyggeri, sikrer vi et fejlfrit og holdbart resultat.
              </p>

              <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-2">
                <a
                  href="tel:+4592900091"
                  className="inline-flex items-center justify-center rounded-full border border-white bg-transparent px-6 py-3 text-sm font-semibold text-white transition-all"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  +45 92 90 00 91
                </a>
              </div>
            </div>

            {/* Right Column: High-converting Webform */}
            <div className="lg:col-span-5 w-full max-w-md mx-auto">
              <div className="rounded-none bg-white border border-slate-200 p-6 sm:p-8 shadow-2xl text-slate-900 space-y-4">
                <div className="space-y-1 text-center lg:text-left">
                  <h2 className="text-xl font-bold text-slate-900">Få et gratis tilbud nu</h2>
                  <p className="text-xs text-slate-600">
                    Udfyld formularen, så vender vi tilbage med et uforpligtende tilbud inden for 24 timer.
                  </p>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="rounded-none bg-slate-100 border border-slate-200 p-4 flex gap-3 text-slate-800">
                    <CheckCircle className="h-5 w-5 text-black shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <h4 className="font-bold">Tak for din forespørgsel!</h4>
                      <p className="mt-1 leading-relaxed">{submitMessage}</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="rounded-none bg-rose-50 border border-rose-200 p-4 flex gap-3 text-rose-800">
                    <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <h4 className="font-bold">Der opstod en fejl</h4>
                      <p className="mt-1 leading-relaxed">{submitMessage}</p>
                    </div>
                  </div>
                )}

                {submitStatus !== 'success' && (
                  <form onSubmit={handleFormSubmit} className="space-y-3.5">
                    {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                    <div className="sr-only absolute -top-96 -left-96">
                      <label htmlFor="hero_website_url">Do not fill this field if you are human</label>
                      <input
                        type="text"
                        id="hero_website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleInputChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Name */}
                    <div className="space-y-1">
                      <label htmlFor="hero_name" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">
                        Navn <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="hero_name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Dit fulde navn"
                        className="w-full rounded-none border border-slate-300 px-3 py-2 text-xs text-slate-900 placeholder-slate-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black bg-white"
                      />
                    </div>

                    {/* Email & Phone Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label htmlFor="hero_email" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">
                          E-mail <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="hero_email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="din@email.dk"
                          className="w-full rounded-none border border-slate-300 px-3 py-2 text-xs text-slate-900 placeholder-slate-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black bg-white"
                        />
                      </div>

                      <div className="space-y-1">
                        <label htmlFor="hero_phone" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">
                          Telefon <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="hero_phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="F.eks. 92 90 00 91"
                          className="w-full rounded-none border border-slate-300 px-3 py-2 text-xs text-slate-900 placeholder-slate-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black bg-white"
                        />
                      </div>
                    </div>

                    {/* Service Type Dropdown */}
                    <div className="space-y-1">
                      <label htmlFor="hero_subject" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">
                        Hvad skal males? <span className="text-rose-500">*</span>
                      </label>
                      <select
                        id="hero_subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full rounded-none border border-slate-300 px-3 py-2 text-xs text-slate-900 bg-white focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                      >
                        <option value="Indendørs Maling">Indendørs Maling</option>
                        <option value="Udendørs Maling">Udendørs Maling</option>
                        <option value="Tapetsering & Filt">Tapetsering & Filt</option>
                        <option value="Erhvervsmaling">Erhvervsmaling</option>
                        <option value="Andet / Ved ikke">Andet / Ved ikke</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div className="space-y-1">
                      <label htmlFor="hero_message" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">
                        Kort beskrivelse <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        id="hero_message"
                        name="message"
                        required
                        rows="3"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="F.eks. Maling af 3-værelses lejlighed på 80 kvm..."
                        className="w-full rounded-none border border-slate-300 px-3 py-2 text-xs text-slate-900 placeholder-slate-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black bg-white resize-none"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex items-center justify-center rounded-full bg-sky-400 hover:bg-sky-500 px-5 py-3 text-sm font-semibold text-white shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {submitting ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Indsender...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-3.5 w-3.5" />
                          Få gratis tilbud nu
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* USPs Section */}
      <section className="py-16 bg-slate-50 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {usps.map((usp, idx) => (
              <div
                key={idx}
                className="flex flex-col items-center text-center gap-4"
              >
                <div className="shrink-0">
                  {usp.icon}
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-bold text-slate-900">{usp.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{usp.description}</p>
                </div>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center max-w-3xl mx-auto space-y-4">
            <p className="text-3xl font-semibold text-slate-900 sm:text-4xl">
              Professionelle malerydelser til ethvert behov
            </p>
            <p className="text-lg text-slate-600">
              Vi løser alle typer maleropgaver med fokus på kvalitet, holdbarhed og flot finish. Se et udvalg af vores mest populære ydelser herunder.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={150} className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-2">
            {visibleServices.map((service) => (
              <div
                key={service.id}
                className="group flex flex-col overflow-hidden rounded-xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300"
              >
                <div className="aspect-video w-full overflow-hidden bg-slate-100 relative">
                  <img
                    src={service.image?.[0] || 'https://assets.auxodev.dk/assets/8102f19c-aab0-44cc-aa15-a120b6503274/548826e2-2df9-4b98-b3e3-fdc54ae27250.jpg'}
                    alt={service.title?.da}
                    className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-black transition-colors">
                    {service.title?.da}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-3 leading-relaxed flex-1">
                    {service.description?.da}
                  </p>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <Link
                      to={`/ydelser/${service.slug?.da || ''}`}
                      className="inline-flex items-center text-sm font-semibold text-black hover:text-slate-800"
                    >
                      Læs mere
                      <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </ScrollReveal>

          {displayServices.length > 2 && (
            <ScrollReveal delay={200} className="mt-12 text-center">
              <button
                onClick={() => setShowAllServices(!showAllServices)}
                className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 transition-all cursor-pointer"
              >
                {showAllServices ? 'Vis færre ydelser' : 'Vis flere ydelser'}
              </button>
            </ScrollReveal>
          )}
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 block">
                Bliv inspireret
              </span>
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Før & efter resultater
              </h2>
              <p className="mt-3 text-lg text-slate-600">
                Se den markante forskel, vores professionelle malerarbejde gør. Træk i skyderne for at sammenligne før- og efter-billederne af vores nyligt afsluttede projekter.
              </p>
            </div>
            <div className="shrink-0">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center rounded-full bg-slate-900 px-8 py-4 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 transition-all cursor-pointer w-full md:w-auto"
              >
                Få et uforpligtende tilbud på dit projekt
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Projekt: Indvendigt rum med blå væg */}
            <ScrollReveal delay={100} className="flex flex-col space-y-3">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/02-interior-room-before.webp"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/04-interior-blue-wall-after.webp"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center">
                <h3 className="text-base font-semibold text-slate-900">Indvendigt rum med blå væg</h3>
              </div>
            </ScrollReveal>

            {/* Projekt: Grå indvendig væg */}
            <ScrollReveal delay={200} className="flex flex-col space-y-3">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/06-grey-wall-before-or-during.webp"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/05-grey-wall-after.webp"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center">
                <h3 className="text-base font-semibold text-slate-900">Grå indvendig væg</h3>
              </div>
            </ScrollReveal>

            {/* Projekt: Udvendig træfacade */}
            <ScrollReveal delay={300} className="flex flex-col space-y-3">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/08-exterior-wood-facade-before-or-during.webp"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/07-exterior-wood-facade-after.webp"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center">
                <h3 className="text-base font-semibold text-slate-900">Udvendig træfacade</h3>
              </div>
            </ScrollReveal>

            {/* Projekt: Hvidt skab/møbel */}
            <ScrollReveal delay={100} className="flex flex-col space-y-3">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/10-cabinet-before.webp"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/09-cabinet-after.webp"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center">
                <h3 className="text-base font-semibold text-slate-900">Hvidt skab/møbel</h3>
              </div>
            </ScrollReveal>

            {/* Project 1 */}
            <ScrollReveal delay={200} className="flex flex-col space-y-3">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/facade-og-terasse-fa-r.jpeg"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/facade-og-terasse-efter.jpeg"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center">
                <h3 className="text-base font-semibold text-slate-900">Facade & Træterrasse</h3>
              </div>
            </ScrollReveal>

            {/* Project 2 */}
            <ScrollReveal delay={300} className="flex flex-col space-y-3">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/indenda-rs-malerarbejde-fa-r.jpg"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/download.jpg"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center">
                <h3 className="text-base font-semibold text-slate-900">Indendørs malerarbejde</h3>
              </div>
            </ScrollReveal>

            {/* Project 3 */}
            <ScrollReveal delay={100} className="flex flex-col space-y-3">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm">
                <BeforeAfterSlider
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/img-2086_1.png"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/8102f19c-aab0-44cc-aa15-a120b6503274/img-2087_1.png"
                  beforeLabel="Før"
                  afterLabel="Efter"
                />
              </div>
              <div className="text-center">
                <h3 className="text-base font-semibold text-slate-900">Træværk & Vinduer</h3>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Pricing Teaser Section */}
      <section className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 block">Gennemsigtighed</span>
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Hvad koster professionelt malerarbejde?
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Vi tror på fuld gennemsigtighed. Derfor arbejder vi med faste, overskuelige fra-priser pr. kvadratmeter og enhed, så du nemt kan kvalificere dit budget.
              </p>
              <div className="pt-2">
                <Link
                  to="/prisestimater"
                  className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 transition-all cursor-pointer"
                >
                  Prøv vores prisberegner
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3 shadow-xs">
                <div className="flex justify-between items-start">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Forarbejde</span>
                  <span className="text-lg font-extrabold text-slate-900">Fra 85 kr. / m²</span>
                </div>
                <h3 className="font-bold text-slate-900 text-sm sm:text-base">Fuldspartling (2 lag)</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Giver helt glatte, jævne og professionelle vægge klar til filt eller maling.
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3 shadow-xs">
                <div className="flex justify-between items-start">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Vægbeklædning</span>
                  <span className="text-lg font-extrabold text-slate-900">Fra 75 kr. / m²</span>
                </div>
                <h3 className="font-bold text-slate-900 text-sm sm:text-base">Opsætning af glasfilt</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Professionel opsætning af glasfilt, der sikrer mod revner og giver et flot, ensartet look.
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3 shadow-xs">
                <div className="flex justify-between items-start">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Malerarbejde</span>
                  <span className="text-lg font-extrabold text-slate-900">Fra 65 kr. / m²</span>
                </div>
                <h3 className="font-bold text-slate-900 text-sm sm:text-base">Maling af vægge (2 lag)</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Professionel dækkeevne med slidstærk kvalitetsmaling i den glans, du ønsker.
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3 shadow-xs">
                <div className="flex justify-between items-start">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Malerarbejde</span>
                  <span className="text-lg font-extrabold text-slate-900">Fra 55 kr. / m²</span>
                </div>
                <h3 className="font-bold text-slate-900 text-sm sm:text-base">Maling af lofter (2 lag)</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Helmat loftmaling for et ensartet, refleksfrit resultat uden synlige rullespor.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Teaser Section */}
      {displayGallery.length > 0 && (
        <section className="py-20 bg-slate-50 border-t border-slate-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <ScrollReveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div className="space-y-3">
                <h2 className="text-xs font-bold uppercase tracking-widest text-black">Vores arbejde</h2>
                <p className="text-3xl font-semibold text-slate-900 sm:text-4xl">
                  Se vores seneste projekter
                </p>
                <p className="text-lg text-slate-600 max-w-2xl">
                  Vi sætter en ære i vores arbejde. Her er et lille udpluk af nyligt afsluttede maleropgaver, som vi har udført for glade kunder.
                </p>
              </div>
              <Link
                to="/galleri"
                className="inline-flex items-center justify-center rounded-full bg-white border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 shadow-sm transition-all shrink-0"
              >
                Se hele galleriet
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </ScrollReveal>

            <ScrollReveal delay={150} className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {displayGallery.map((item) => (
                <Link
                  key={item.id}
                  to="/galleri"
                  className="group relative overflow-hidden rounded-xl bg-white shadow-sm border border-slate-200 cursor-pointer block"
                >
                  <div className="aspect-4/3 w-full overflow-hidden bg-slate-100">
                    <img
                      src={item.image?.[0]}
                      alt="Malerarbejde reference"
                      className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="rounded-full bg-white/90 p-3 text-slate-900 shadow-md transform scale-90 group-hover:scale-100 transition-transform duration-300">
                      <ZoomIn className="h-6 w-6" />
                    </div>
                  </div>
                </Link>
              ))}
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {displayTestimonials.length > 0 && (
        <section className="py-20 bg-white border-t border-slate-200">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <ScrollReveal className="text-center max-w-3xl mx-auto space-y-4 mb-14">
              <h2 className="text-xs font-bold uppercase tracking-widest text-black">Anmeldelser</h2>
              <p className="text-3xl font-semibold text-slate-900 sm:text-4xl">
                Hvad siger vores kunder?
              </p>
              <p className="text-lg text-slate-600">
                Kundernes tilfredshed er vores største succes. Læs anmeldelser fra nogle af de mange private og erhvervskunder, vi har hjulpet.
              </p>
            </ScrollReveal>

            {/* Testimonials Carousel with Arrows */}
            <ScrollReveal delay={150} className="relative mt-12 px-4 md:px-12">
              {displayTestimonials.length > 1 && (
                <>
                  {/* Left Arrow */}
                  <button
                    onClick={prevTestimonial}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white border border-slate-200 shadow-md hover:bg-slate-50 text-slate-700 hover:text-black transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400"
                    aria-label="Forrige anmeldelse"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>

                  {/* Right Arrow */}
                  <button
                    onClick={nextTestimonial}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white border border-slate-200 shadow-md hover:bg-slate-50 text-slate-700 hover:text-black transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-400"
                    aria-label="Næste anmeldelse"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}

              {/* Testimonials Grid (Responsive sliding window) */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {visibleTestimonials.map((testimonial, index) => (
                  <div
                    key={testimonial.displayKey}
                    className={`flex flex-col justify-between rounded-2xl bg-slate-50 p-8 border border-slate-100 relative transition-all duration-500 ${
                      index === 0 ? 'block' : index === 1 ? 'hidden md:flex' : 'hidden lg:flex'
                    }`}
                  >
                    <Quote className="absolute top-6 right-8 h-10 w-10 text-slate-200/80 shrink-0" />
                    <div className="space-y-4 relative z-10">
                      <div className="flex gap-1">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} className="h-5 w-5 fill-sky-400 text-sky-400" />
                        ))}
                      </div>
                      <p className="text-slate-700 italic leading-relaxed text-sm">
                        "{testimonial.text?.da}"
                      </p>
                    </div>
                    <div className="mt-6 pt-6 border-t border-slate-200/60 flex items-center justify-between">
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm">{testimonial.name}</h4>
                        <p className="text-xs text-slate-500 mt-0.5">{testimonial.project_type?.da}</p>
                      </div>
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800">
                        Verificeret
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* Bottom Contact Form Section */}
      <section className="relative overflow-hidden bg-slate-900 py-20 text-white border-t border-slate-800">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.05),transparent_50%)]" />
        
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Info & Trust */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <span className="text-xs font-bold uppercase tracking-widest text-sky-400 block">Klar til at starte?</span>
                <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
                  Skal vi hjælpe dig med dit næste malerprojekt?
                </h2>
                <p className="text-slate-300 leading-relaxed text-base sm:text-lg">
                  Uanset om opgaven er stor eller lille, står vi klar til at rådgive dig og give et uforpligtende, skriftligt tilbud. Kontakt os i dag, og lad os tage en snak.
                </p>
              </div>

              {/* Direct Contact Info */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-sky-400 shrink-0">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Ring direkte</p>
                    <a href="tel:+4592900091" className="text-base font-bold text-white hover:text-sky-400 transition-colors">
                      +45 92 90 00 91
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-sky-400 shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Send en mail</p>
                    <a href="mailto:info@probyg.tech" className="text-base font-bold text-white hover:text-sky-400 transition-colors">
                      info@probyg.tech
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-800 text-sky-400 shrink-0">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Vores adresse</p>
                    <p className="text-sm font-semibold text-slate-200">
                      Njalsgade 21F 2., 2300 København S
                    </p>
                  </div>
                </div>
              </div>

              {/* Trust badges */}
              <div className="border-t border-slate-800 pt-6 space-y-3">
                <div className="flex items-center gap-2.5 text-xs text-slate-300">
                  <CheckCircle className="h-4 w-4 text-sky-400 shrink-0" />
                  <span>Gratis og uforpligtende tilbud inden for 24 timer</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-slate-300">
                  <CheckCircle className="h-4 w-4 text-sky-400 shrink-0" />
                  <span>100% tilfredshedsgaranti på alt udført arbejde</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-slate-300">
                  <CheckCircle className="h-4 w-4 text-sky-400 shrink-0" />
                  <span>Faglærte, lokale malere med over 15 års erfaring</span>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form Card */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl text-slate-900 space-y-6 border border-slate-100">
                <div className="space-y-1">
                  <h3 className="text-xl font-bold text-slate-900">Send os en besked</h3>
                  <p className="text-xs text-slate-500">
                    Udfyld formularen, så kontakter vi dig hurtigst muligt for en uforpligtende snak.
                  </p>
                </div>

                {/* Status Messages */}
                {bottomSubmitStatus === 'success' && (
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex gap-3 text-slate-800">
                    <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <h4 className="font-bold">Tak for din henvendelse!</h4>
                      <p className="mt-1 leading-relaxed">{bottomSubmitMessage}</p>
                    </div>
                  </div>
                )}

                {bottomSubmitStatus === 'error' && (
                  <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 flex gap-3 text-rose-800">
                    <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <h4 className="font-bold">Der opstod en fejl</h4>
                      <p className="mt-1 leading-relaxed">{bottomSubmitMessage}</p>
                    </div>
                  </div>
                )}

                {bottomSubmitStatus !== 'success' && (
                  <form onSubmit={handleBottomFormSubmit} className="space-y-4">
                    {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                    <div className="sr-only absolute -top-96 -left-96">
                      <label htmlFor="bottom_website_url">Do not fill this field if you are human</label>
                      <input
                        type="text"
                        id="bottom_website_url"
                        name="website_url"
                        value={bottomFormData.website_url}
                        onChange={handleBottomInputChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Name */}
                    <div className="space-y-1">
                      <label htmlFor="bottom_name" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">
                        Navn <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="bottom_name"
                        name="name"
                        required
                        value={bottomFormData.name}
                        onChange={handleBottomInputChange}
                        placeholder="Dit fulde navn"
                        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 bg-slate-50/50"
                      />
                    </div>

                    {/* Email & Phone Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label htmlFor="bottom_email" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">
                          E-mail <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="bottom_email"
                          name="email"
                          required
                          value={bottomFormData.email}
                          onChange={handleBottomInputChange}
                          placeholder="din@email.dk"
                          className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 bg-slate-50/50"
                        />
                      </div>

                      <div className="space-y-1">
                        <label htmlFor="bottom_phone" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">
                          Telefon <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="tel"
                          id="bottom_phone"
                          name="phone"
                          required
                          value={bottomFormData.phone}
                          onChange={handleBottomInputChange}
                          placeholder="F.eks. 92 90 00 91"
                          className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 bg-slate-50/50"
                        />
                      </div>
                    </div>

                    {/* Service Type Dropdown */}
                    <div className="space-y-1">
                      <label htmlFor="bottom_subject" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">
                        Hvad skal males? <span className="text-rose-500">*</span>
                      </label>
                      <select
                        id="bottom_subject"
                        name="subject"
                        required
                        value={bottomFormData.subject}
                        onChange={handleBottomInputChange}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-xs text-slate-900 bg-slate-50/50 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                      >
                        <option value="Indendørs Maling">Indendørs Maling</option>
                        <option value="Udendørs Maling">Udendørs Maling</option>
                        <option value="Tapetsering & Filt">Tapetsering & Filt</option>
                        <option value="Erhvervsmaling">Erhvervsmaling</option>
                        <option value="Andet / Ved ikke">Andet / Ved ikke</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div className="space-y-1">
                      <label htmlFor="bottom_message" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block">
                        Kort beskrivelse af projektet <span className="text-rose-500">*</span>
                      </label>
                      <textarea
                        id="bottom_message"
                        name="message"
                        required
                        rows="4"
                        value={bottomFormData.message}
                        onChange={handleBottomInputChange}
                        placeholder="Beskriv kort din opgave (f.eks. antal rum, kvadratmeter, eller særlige ønsker)..."
                        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500 bg-slate-50/50 resize-none"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={bottomSubmitting}
                      className="w-full inline-flex items-center justify-center rounded-full bg-sky-400 hover:bg-sky-500 px-5 py-3 text-sm font-semibold text-white shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {bottomSubmitting ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Indsender...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-3.5 w-3.5" />
                          Send forespørgsel
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
